<template>
  <Teleport to="body">
    <div
      v-if="props.show"
      class="fixed inset-0 z-[1700] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      :style="{ zIndex: resolvedZIndex }"
      role="dialog"
      aria-modal="true"
      :aria-label="`Select ${mediaLabel}`"
    >
      <div class="w-full max-w-6xl max-h-[88vh] rounded-2xl border border-zinc-200 bg-white shadow-2xl overflow-hidden">
      <div class="flex items-center justify-between gap-3 border-b border-zinc-200 px-5 py-4">
        <h3 class="text-lg font-semibold text-zinc-900">
          Select {{ mediaLabel }}
        </h3>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="rounded-md bg-[#2D8CFF] px-3 py-2 text-sm font-semibold text-white hover:bg-[#1f77e6]"
            @click="videoMediaUploadModalOpen = true"
          >
            Upload {{ mediaLabel }}
          </button>
          <button
            type="button"
            class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
            @click="emit('close')"
          >
            Close
          </button>
        </div>
      </div>

      <div class="border-b border-zinc-200 px-5 py-3 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
        <PlatformaSelect
          v-model="videoMediaCurrentFolderModel"
          :options="videoMediaFolderOptions"
          placeholder="Select folder"
        />
        <input
          v-model="videoMediaSearchQuery"
          type="text"
          class="w-full border rounded px-3 py-2 text-sm"
          :placeholder="`Search ${mediaLabelPlural.toLowerCase()}`"
        >
      </div>

      <div class="p-5 overflow-y-auto max-h-[62vh] app-scroll">
        <div
          v-if="videoMediaLoading"
          class="py-10 text-center text-sm text-zinc-500"
        >
          Loading {{ mediaLabelPlural.toLowerCase() }}...
        </div>
        <div
          v-else-if="videoMediaFilteredFiles.length === 0"
          class="py-10 text-center text-sm text-zinc-500"
        >
          No {{ mediaLabelPlural.toLowerCase() }} found in this folder.
        </div>
        <div
          v-else
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          <button
            v-for="videoFile in videoMediaFilteredFiles"
            :key="`video-media-${videoFile.id || videoFile.file_id || videoFile.fileId}`"
            type="button"
            class="group rounded-xl border border-zinc-200 bg-white hover:border-[#2D8CFF] hover:shadow-sm transition overflow-hidden text-left"
            @click="onSelectMedia(videoFile)"
          >
            <div class="aspect-video bg-zinc-100 relative overflow-hidden">
              <img
                v-if="resolveMediaPreviewUrl(videoFile)"
                :src="resolveMediaPreviewUrl(videoFile)"
                :alt="`${mediaLabel} preview`"
                class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              >
              <div
                v-else
                class="h-full w-full flex items-center justify-center text-xs text-zinc-500"
              >
                {{ selectedMediaType === 'video' ? 'Processing...' : 'Preview unavailable' }}
              </div>
            </div>
            <div class="px-3 py-2">
              <p class="text-sm font-semibold text-zinc-800 truncate">
                {{ videoFile.file_name || videoFile.fileName || `${mediaLabel} file` }}
              </p>
              <p class="mt-0.5 text-xs text-zinc-500 truncate">
                {{ (videoFile.mime_type || videoFile.mimeType || selectedMediaType).toLowerCase() }}
              </p>
            </div>
          </button>
        </div>
      </div>
      </div>

      <CreatorMediaUploadModal
        :show="videoMediaUploadModalOpen"
        :folder-id="videoMediaCurrentFolderId"
        :z-index="resolvedZIndex + 1"
        @close="videoMediaUploadModalOpen = false"
        @uploaded="onVideoMediaUploaded"
      />
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import PlatformaSelect from '@/Components/PlatformaSelect.vue'
import CreatorMediaUploadModal from '@/Components/CreatorMediaUploadModal.vue'

const props = defineProps({
  show: { type: Boolean, default: false },
  apiProxy: { type: String, default: '' },
  requestedType: { type: String, default: 'platforma_media' },
  initialFolderId: { type: [String, Number], default: '' },
  mediaType: { type: String, default: 'video' },
  zIndex: { type: Number, default: 1700 },
})

const emit = defineEmits(['close', 'select', 'folder-change'])

const videoMediaUploadModalOpen = ref(false)
const resolvedZIndex = computed(() => {
  const value = Number(props.zIndex)
  return Number.isFinite(value) && value > 0 ? value : 1700
})
const videoMediaFolders = ref([])
const videoMediaCurrentFolderId = ref('')
const videoMediaFiles = ref([])
const videoMediaSearchQuery = ref('')
const videoMediaLoading = ref(false)

const selectedMediaType = computed(() =>
  String(props.mediaType || '').trim().toLowerCase() === 'image' ? 'image' : 'video'
)
const mediaLabel = computed(() => selectedMediaType.value === 'image' ? 'Image' : 'Video')
const mediaLabelPlural = computed(() => selectedMediaType.value === 'image' ? 'Images' : 'Videos')

const resolveStoredMediaUrl = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized || normalized === 'null' || normalized === 'undefined') return ''
  return normalized
}

const normalizeVideoType = (value, fallback = 'platforma_media') => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'upload') return 'upload'
  if (normalized === 'platforma_media') return 'platforma_media'
  return fallback
}

const apiBase = computed(() => {
  const raw = String(props.apiProxy ?? '').trim()
  if (!raw || raw.toLowerCase() === 'undefined' || raw.toLowerCase() === 'null') return ''
  return raw.replace(/\/+$/, '')
})

const videoMediaFolderOptions = computed(() =>
  (Array.isArray(videoMediaFolders.value) ? videoMediaFolders.value : []).map((folder) => ({
    value: String(folder?.id ?? ''),
    label: String(folder?.name || 'Folder'),
  }))
)

const videoMediaCurrentFolderModel = computed({
  get: () => String(videoMediaCurrentFolderId.value || ''),
  set: (value) => {
    void onVideoMediaFolderChange(value)
  },
})

const videoMediaFilteredFiles = computed(() => {
  const query = String(videoMediaSearchQuery.value || '').trim().toLowerCase()
  const rows = Array.isArray(videoMediaFiles.value) ? videoMediaFiles.value : []
  return rows.filter((file) => {
    const fileType = String(file?.file_type || file?.fileType || '').trim().toLowerCase()
    if (fileType && fileType !== selectedMediaType.value) return false
    const mime = String(file?.mime_type || file?.mimeType || '').trim().toLowerCase()
    if (!fileType && mime && !mime.startsWith(`${selectedMediaType.value}/`)) return false
    if (!query) return true
    const name = String(file?.file_name || file?.fileName || '').trim().toLowerCase()
    return name.includes(query)
  })
})

const resolvedRequestedType = computed(() =>
  normalizeVideoType(props.requestedType, 'platforma_media')
)

const resolveMediaPreviewUrl = (file) => {
  if (selectedMediaType.value === 'image') {
    const secureImage = resolveStoredMediaUrl(
      file?.secure_image_url ||
      file?.secureImageUrl ||
      file?.secure_file_url ||
      file?.secureFileUrl ||
      file?.file_url ||
      file?.fileUrl
    )
    if (secureImage) return secureImage
    const fileId = String(file?.file_id || file?.fileId || file?.media_file_id || file?.mediaFileId || '').trim()
    return fileId ? `${apiBase.value || ''}/api/image/${encodeURIComponent(fileId)}` : ''
  }
  const secureThumb = resolveStoredMediaUrl(file?.secure_thumbnail_url || file?.secureThumbnailUrl || file?.thumbnail_url || file?.thumbnailUrl)
  if (secureThumb) return secureThumb
  return ''
}

const fetchVideoMediaFolders = async () => {
  const response = await fetch(`${apiBase.value || ''}/api/list-folders`, {
    method: 'GET',
    credentials: 'include',
  })
  if (!response.ok) throw new Error(`Failed to load folders (${response.status})`)
  const data = await response.json()
  const folders = Array.isArray(data?.folders) ? data.folders : []
  if (folders.length) return folders
  const createResponse = await fetch(`${apiBase.value || ''}/api/create-folder`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: 'Main', parentId: null }),
  })
  if (!createResponse.ok) return folders
  const retryResponse = await fetch(`${apiBase.value || ''}/api/list-folders`, {
    method: 'GET',
    credentials: 'include',
  })
  if (!retryResponse.ok) return folders
  const retryData = await retryResponse.json()
  return Array.isArray(retryData?.folders) ? retryData.folders : folders
}

const fetchVideoMediaFiles = async (folderId) => {
  const normalized = String(folderId || '').trim()
  if (!normalized) return []
  const response = await fetch(`${apiBase.value || ''}/api/list-files?folderId=${encodeURIComponent(normalized)}`, {
    method: 'GET',
    credentials: 'include',
  })
  if (!response.ok) throw new Error(`Failed to load files (${response.status})`)
  const data = await response.json()
  return Array.isArray(data?.files) ? data.files : []
}

const loadVideoMediaPickerData = async () => {
  videoMediaLoading.value = true
  try {
    const folders = await fetchVideoMediaFolders()
    videoMediaFolders.value = folders
    const selectedFolder = folders.find((folder) => String(folder?.id ?? '') === String(videoMediaCurrentFolderId.value || ''))
    if (!selectedFolder) {
      const mainFolder = folders.find((folder) => String(folder?.name || '').trim().toLowerCase() === 'main' && !folder?.parent_id)
      videoMediaCurrentFolderId.value = String(mainFolder?.id ?? folders?.[0]?.id ?? '')
    }
    videoMediaFiles.value = await fetchVideoMediaFiles(videoMediaCurrentFolderId.value)
  } catch (error) {
    console.error('[PageEditorVideoElementModal] load failed:', error)
    videoMediaFiles.value = []
  } finally {
    videoMediaLoading.value = false
  }
}

const onVideoMediaFolderChange = async (folderId) => {
  const normalized = String(folderId || '').trim()
  if (!normalized) {
    videoMediaCurrentFolderId.value = ''
    videoMediaFiles.value = []
    emit('folder-change', '')
    return
  }
  videoMediaCurrentFolderId.value = normalized
  emit('folder-change', normalized)
  videoMediaLoading.value = true
  try {
    videoMediaFiles.value = await fetchVideoMediaFiles(normalized)
  } catch (error) {
    console.error('[PageEditorVideoElementModal] folder load failed:', error)
    videoMediaFiles.value = []
  } finally {
    videoMediaLoading.value = false
  }
}

const onVideoMediaUploaded = async () => {
  videoMediaUploadModalOpen.value = false
  await onVideoMediaFolderChange(videoMediaCurrentFolderId.value)
}

const onSelectMedia = (file) => {
  emit('select', {
    file,
    requestedType: resolvedRequestedType.value,
    mediaType: selectedMediaType.value,
  })
  emit('close')
}

watch(
  () => props.initialFolderId,
  (nextValue) => {
    const normalized = String(nextValue || '').trim()
    if (normalized && !videoMediaCurrentFolderId.value) {
      videoMediaCurrentFolderId.value = normalized
    }
  },
  { immediate: true }
)

watch(
  () => props.show,
  async (open) => {
    if (!open) {
      videoMediaUploadModalOpen.value = false
      return
    }
    await loadVideoMediaPickerData()
  }
)
</script>
